import { useState } from 'react';
import { useNavigate } from 'react-router';
import { Surface, Button, Alert, Input } from '../../ui/components';
import { ShieldCheck, Users, BarChart3, Settings, Eye, EyeOff, AlertTriangle } from 'lucide-react';
// Admin credentials (demo mode)
const ADMIN_CREDENTIALS = {
  email: 'mark@skramco.com',
  password: 'admin123',
};

const AdminLoginPage = () => {
  const navigate = useNavigate();
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);

  const handleLogin = async (e) => {
    e.preventDefault();
    setError('');
    setLoading(true);

    // Simulate network delay
    await new Promise(resolve => setTimeout(resolve, 500));

    if (email.toLowerCase() === ADMIN_CREDENTIALS.email && password === ADMIN_CREDENTIALS.password) {
      // Store admin session
      localStorage.setItem('lk_admin_session', JSON.stringify({
        email: email,
        role: 'admin',
        loginTime: new Date().toISOString(),
      }));
      navigate('/admin/dashboard');
    } else {
      setError('Invalid credentials. Access is restricted to authorized administrators only.');
    }
    setLoading(false);
  };

  const FeatureRow = ({ icon: Icon, title, desc }) => (
    <div style={{ display: 'flex', alignItems: 'center', gap: 'var(--lk-space-3)', padding: 'var(--lk-space-3)', borderRadius: 'var(--lk-radius-md)', background: 'rgba(255,255,255,0.05)' }}>
      <div style={{ width: 44, height: 44, borderRadius: 'var(--lk-radius-md)', background: 'rgba(255,255,255,0.1)', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
        <Icon size={24} />
      </div>
      <div style={{ textAlign: 'left' }}>
        <div style={{ fontWeight: 600, fontSize: 'var(--lk-text-sm)' }}>{title}</div>
        <div style={{ fontSize: 'var(--lk-text-xs)', opacity: 0.8 }}>{desc}</div>
      </div>
    </div>
  );

  return (
    <div style={{ minHeight: '100vh', background: '#000', display: 'grid', gridTemplateColumns: '1fr 1fr' }}>
      {/* Left Side - Illustration */}
      <div style={{
        background: 'linear-gradient(135deg, #434343 0%, #000000 100%)',
        color: 'white',
        display: 'flex',
        flexDirection: 'column',
        justifyContent: 'center',
        alignItems: 'center',
        padding: 'var(--lk-space-10)',
      }}>
        <div style={{ maxWidth: 500, textAlign: 'center' }}>
          <div style={{ width: 80, height: 80, borderRadius: 'var(--lk-radius-lg)', background: 'rgba(255,255,255,0.1)', display: 'flex', alignItems: 'center', justifyContent: 'center', margin: '0 auto var(--lk-space-6)' }}>
            <ShieldCheck size={48} />
          </div>
          <h1 style={{ fontSize: 'var(--lk-text-4xl)', fontWeight: 700, marginBottom: 'var(--lk-space-3)', letterSpacing: '-0.02em' }}>
            LoanKnow Admin
          </h1>
          <p style={{ fontSize: 'var(--lk-text-lg)', opacity: 0.9, marginBottom: 'var(--lk-space-8)' }}>
            Internal administration portal for authorized LoanKnow employees only.
          </p>
          <div style={{ display: 'flex', flexDirection: 'column', gap: 'var(--lk-space-3)' }}>
            <FeatureRow icon={Users} title="User Management" desc="Manage all system users and roles" />
            <FeatureRow icon={BarChart3} title="System Analytics" desc="Monitor platform performance" />
            <FeatureRow icon={Settings} title="Configuration" desc="System settings and controls" />
          </div>
        </div>
      </div>

      {/* Right Side - Login Form */}
      <div style={{
        display: 'flex',
        flexDirection: 'column',
        justifyContent: 'center',
        alignItems: 'center',
        padding: 'var(--lk-space-10)',
        background: 'white',
      }}>
        <div style={{ width: '100%', maxWidth: 400 }}>
          <div style={{ textAlign: 'center', marginBottom: 'var(--lk-space-6)' }}>
            <div style={{ width: 64, height: 64, borderRadius: 'var(--lk-radius-md)', background: '#000', display: 'flex', alignItems: 'center', justifyContent: 'center', margin: '0 auto var(--lk-space-3)' }}>
              <ShieldCheck size={32} color="white" />
            </div>
            <h2 style={{ fontWeight: 700, marginBottom: 4 }}>Admin Portal</h2>
            <p style={{ color: 'var(--lk-text-tertiary)', margin: 0 }}>Internal administration access</p>
          </div>

          {error && (
            <Alert variant="error" style={{ marginBottom: 'var(--lk-space-4)' }}>
              {error}
            </Alert>
          )}

          <form onSubmit={handleLogin}>
            <Input
              label="Email Address"
              type="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              placeholder="admin@loanknow.com"
              autoComplete="email"
              required
            />
            <div style={{ marginTop: 'var(--lk-space-3)', position: 'relative' }}>
              <Input
                label="Password"
                type={showPassword ? 'text' : 'password'}
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                placeholder="Enter your password"
                autoComplete="current-password"
                required
              />
              <button
                type="button"
                onClick={() => setShowPassword(!showPassword)}
                style={{ position: 'absolute', right: 10, top: 34, background: 'none', border: 'none', cursor: 'pointer', color: 'var(--lk-text-tertiary)', padding: 4 }}
              >
                {showPassword ? <EyeOff size={18} /> : <Eye size={18} />}
              </button>
            </div>

            <Button
              type="submit"
              variant="primary"
              size="lg"
              loading={loading}
              style={{ width: '100%', marginTop: 'var(--lk-space-4)', background: '#000', borderColor: '#000' }}
            >
              Sign In to Admin Portal
            </Button>
          </form>

          <Surface variant="muted" padding="var(--lk-space-3)" style={{ marginTop: 'var(--lk-space-6)', textAlign: 'center' }}>
            <p style={{ fontSize: 'var(--lk-text-xs)', color: 'var(--lk-text-tertiary)', margin: 0, display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 4, flexWrap: 'wrap' }}>
              <AlertTriangle size={14} />
              This portal is restricted to authorized LoanKnow employees only.
              Unauthorized access attempts are logged and monitored.
            </p>
          </Surface>
        </div>
      </div>
    </div>
  );
};

export default AdminLoginPage;
